import { Injectable, computed, inject, signal } from '@angular/core';
import { CompanyContextService } from '../services/company-context.service';
import { DemoDataStore } from './demo-data-store';
import { DEMO_COMPANY_ID, DemoFlow } from './demo.constants';

const AUTH_TOKEN_KEY = 'jl-auth-token';
const AUTH_USER_KEY = 'jl-auth-user';
const AUTH_REMEMBER_KEY = 'jl-auth-remember';

/** base64url-encode an ASCII string (JWT segment). */
function b64url(input: string): string {
  return btoa(input).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** A signature-less JWT with a far-future expiry, enough for the auth guard. */
function buildDemoJwt(): string {
  const header = b64url(JSON.stringify({ alg: 'none', typ: 'JWT' }));
  const payload = b64url(
    JSON.stringify({
      sub: 'demo-user',
      publicId: 'demo-user',
      name: 'Demo Kullanici',
      email: 'demo@jestiyon.com',
      exp: 4102444800, // 2100-01-01
    }),
  );
  return `${header}.${payload}.demo`;
}

/**
 * Owns demo-mode state and the one-time environment setup needed for the
 * scripted tutorial to run inside the real authenticated shell.
 *
 * Activating a flow installs a fake auth session + selected company (so the
 * auth guard passes and the shell boots straight to the dashboard) and resets
 * the in-memory data store to its seeded state.
 */
@Injectable({ providedIn: 'root' })
export class DemoModeService {
  private readonly companyContext = inject(CompanyContextService);
  private readonly store = inject(DemoDataStore);

  private readonly _active = signal(false);
  private readonly _flow = signal<DemoFlow | null>(null);

  readonly active = this._active.asReadonly();
  readonly flow = this._flow.asReadonly();
  readonly isPlaying = computed(() => this._active() && this._flow() !== null);

  /** Install the fake session and prepare data for a flow. Idempotent per call. */
  activate(flow: DemoFlow): void {
    this.installFakeSession();
    this.store.reset();
    this._flow.set(flow);
    this._active.set(true);
  }

  deactivate(): void {
    this._active.set(false);
    this._flow.set(null);
  }

  private installFakeSession(): void {
    try {
      localStorage.setItem(AUTH_TOKEN_KEY, buildDemoJwt());
      localStorage.setItem(
        AUTH_USER_KEY,
        JSON.stringify({ publicId: 'demo-user', fullName: 'Demo Kullanıcı', email: 'demo@jestiyon.com' }),
      );
      localStorage.setItem(AUTH_REMEMBER_KEY, 'true');
    } catch {
      /* storage unavailable — ignore */
    }
    this.companyContext.setSelectedCompanyId(DEMO_COMPANY_ID);
  }
}
